import { Header } from '@components/Header';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TTeamMembersChange } from '@modals/Team/EditTeamMembersModal/types/TTeamMembersChange';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FindOneTeamResponseDto } from 'app/API';
import { cancelAndInvalidateQueries } from 'app/queries/cancelAndInvalidateQueries';
import { commitTeamMembers } from 'app/queries/teams/commitTeamMembers';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { createFetchTeamBySlugQueryFn } from './api/fetchTeamBySlug';
import { withTeamMembers } from './helpers/withTeamMembers';
import { withoutTeamMembers } from './helpers/withoutTeamMembers';
import { MainContent } from './styles/MainContent';
import { TeamInformation } from './TeamInformation';
import { TeamMembersTable } from './TeamMembersTable';
import { TOptimisticTeamContext } from './types/TOptimisticTeamContext';
import { TTeamMembersUpdate } from './types/TTeamMembersUpdate';

export const TeamDetailsPage = () => {
  const { call } = useAPI();
  const { teamSlug } = useParams<{ teamSlug: string }>();

  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const organizationId = organization?.id ?? '';
  const orgSlug = organization?.slug ?? '';
  const slug = teamSlug ?? '';
  const teamDetailKey = teamsKeys.detailBySlug(organizationId, slug);

  const teamQuery = useQuery({
    queryKey: teamDetailKey,
    queryFn: createFetchTeamBySlugQueryFn(call),
    enabled: organizationId !== '' && slug !== '',
  });

  const refreshTeam = () => cancelAndInvalidateQueries(queryClient, teamDetailKey);

  const refreshTeamsList = () =>
    queryClient.invalidateQueries({ queryKey: teamsKeys.listsByOrganization(organizationId) });

  const handleSaved = (savedSlug: string) => {
    void refreshTeamsList();

    if (savedSlug === slug) {
      void refreshTeam();
      return;
    }

    queryClient.removeQueries({ queryKey: teamDetailKey });
    navigate(routes.teamDetails.make(orgSlug, savedSlug), { replace: true });
  };

  const updateMembersMutation = useMutation({
    mutationFn: ({ teamId, added, removed }: TTeamMembersUpdate) =>
      commitTeamMembers(
        {
          teamId,
          added: added.map((member) => member.id),
          removed,
          onError: (message) => toast.error(message || 'Unable to update the team members.'),
        },
        call,
      ),
    onMutate: async ({ added, removed }: TTeamMembersUpdate) => {
      await queryClient.cancelQueries({ queryKey: teamDetailKey });
      const previousTeam = queryClient.getQueryData<FindOneTeamResponseDto>(teamDetailKey);

      queryClient.setQueryData<FindOneTeamResponseDto>(teamDetailKey, (team) =>
        withTeamMembers(withoutTeamMembers(team, removed), added),
      );

      return { previousTeam };
    },
    onError: (_error, _update, context?: TOptimisticTeamContext) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamDetailKey, context.previousTeam);
      }
    },
    onSettled: () => Promise.all([refreshTeam(), refreshTeamsList()]),
  });

  const team = teamQuery.data;
  const title = team?.name ? team.name : 'Team';
  const isBusy = teamQuery.isFetching || updateMembersMutation.isPending;
  const hasInitialError = teamQuery.isError && team === undefined;

  const editMembers = (teamId: string) => {
    openModal(ModalNamesEnum.EditTeamMembersModal, {
      teamId,
      onSuccess: ({ added, removed }: TTeamMembersChange) => {
        closeModal();

        if (added.length === 0 && removed.length === 0) {
          return;
        }

        updateMembersMutation.mutate({ teamId, added, removed });
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{title} - AutoScript</title>
      </Helmet>
      <Header
        title={title}
        buttonLabel={'Edit Team Members'}
        buttonDisabled={!team}
        buttonOnClick={() => team && editMembers(team.id)}
        backRoute={routes.organizationDetails.make(orgSlug)}
      />

      <LinearProgressLoader active={isBusy} />
      <MainContent>
        {hasInitialError ? (
          <PageErrorState
            message="Couldn't load this team"
            actionLabel="Retry"
            actionDisabled={isBusy}
            onAction={() => void teamQuery.refetch()}
          />
        ) : null}
        {team ? <TeamInformation team={team} onSaved={handleSaved} /> : null}
        {team ? (
          <TeamMembersTable
            team={team}
            onRemoveMember={(memberId) =>
              updateMembersMutation.mutate({ teamId: team.id, added: [], removed: [memberId] })
            }
          />
        ) : null}
      </MainContent>
    </>
  );
};
